<template>
  <form @submit.prevent="submit">
    <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 80vh; height: fit-content;">
      <header class="modal-card-head">
        <p class="modal-card-title">
          New Inventory
          <span style="font-size:1rem; text-overflow: elipsis">{{ plant.name }}</span>
        </p>
      </header>
      <section class="modal-card-body">
        <b-field grouped>
          <b-field label="Crate Type">
            <multiselect
              v-model="selectedCrates"
              :options="crateTypes"
              :multiple="true"
              :close-on-select="true"
              placeholder="Pick plant"
              :custom-label="(obj) => obj.name"
              track-by="name"
              style="min-width: 400px;"
            />
          </b-field>
          <b-field label="Date">
            <b-datepicker
              v-model="date"
              name="date"
              :first-day-of-week="1"
              :locale="'en-CA'"
              icon="calendar-alt"
              placeholder="Date"
            />
          </b-field>
        </b-field>
        <b-field v-for="selectedCrate of selectedCrates" :key="selectedCrate.name" grouped>
          <b-field :label="'Number of Clean ' + selectedCrate.name">
            <b-input v-model="cleanCrates[selectedCrate.value]" type="number" placeholder="Clean Crates" min="0" />
            <b-button @click="clearClean">
              Clear
            </b-button>
          </b-field>
          <!--
            A client-specific type is prepared crates only: they are counted clean,
            and a soiled one is declassified back to its root type rather than being
            held as dirty stock of the client type.
          -->
          <b-field v-if="!isClientType(selectedCrate.value)" :label="'Number of Dirty ' + selectedCrate.name">
            <b-input v-model="dirtyCrates[selectedCrate.value]" type="number" placeholder="Dirty Crates" min="0" />
            <b-button @click="clearDirty">
              Clear
            </b-button>
          </b-field>
          <b-field v-if="!isClientType(selectedCrate.value)" :label="'Number to Repair ' + selectedCrate.name">
            <b-input v-model="repairCrates[selectedCrate.value]" type="number" placeholder="Repair Crates" min="0" />
            <b-button @click="clearRepair">
              Clear
            </b-button>
          </b-field>
        </b-field>
      </section>
      <footer class="modal-card-foot">
        <button type="submit" class="button is-info is-fullwidth" :class="{ 'is-loading': saving }" :disabled="saving">
          {{ $t('global.submit') }}
        </button>
      </footer>
    </div>
  </form>
</template>

<script>
import TargetStock from '../../services/v3/targetStock'
import ClientCrateType from '../../services/v3/clientCrateType'
import { UNIVERSAL_CRATE_TYPES, LEGACY_CLIENT_TYPES, VIRTUAL_TYPE_MEMBERS } from '@/helpers/crateTypes'

export default {
  props: {
    plant: {
      type: Object,
      default: null
    },
    // The parent is writing the declaration: the submit button waits.
    saving: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // '5.X' is a reporting pool and is never inventoried directly — the two real
      // sub-types are. Client-specific types are appended in mounted, once the
      // plant's registry rows are known.
      crateTypes: [
        ...UNIVERSAL_CRATE_TYPES.filter(t => !VIRTUAL_TYPE_MEMBERS[t.value]),
        ...LEGACY_CLIENT_TYPES
      ],
      clientCrateTypes: [],
      selectedCrates: [{ name: '5.2', value: '5.2' }],
      output: [],
      date: new Date(),
      crateType: '5.2',
      dirtyCrates: [],
      cleanCrates: [],
      repairCrates: [],
      targets: [],
      types: []
    }
  },
  computed: {
  },
  mounted: async function () {
    const [targets, clientTypes] = await Promise.all([
      TargetStock.get({ id_site: this.plant.id }),
      ClientCrateType.getForSite(this.plant.id)
    ])
    this.targets = targets
    // A client-specific type has to be inventoriable, or its stock can never be
    // given an opening count and the forecast starts it at zero for ever.
    this.clientCrateTypes = clientTypes || []
    this.crateTypes = [...this.crateTypes, ...this.clientCrateTypes.map(t => ({ name: t.name, value: t.name }))]
    if (this.targets && this.targets.length > 0) {
      this.types = JSON.parse(this.targets[0].types)
      this.selectedCrates = []
      if (this.types.map(x => x.name).includes('5.X')) {
        this.selectedCrates.push({ name: '5.2', value: '5.2' })
      }
      this.selectedCrates.push(...this.crateTypes.filter((x) => {
        return this.types.find(e => e.name === x.value)
      }))
      // The plant's registry types are always countable there, whether or not they
      // reached the stored target-stock list.
      this.clientCrateTypes.forEach((clientType) => {
        if (!this.selectedCrates.some(x => x.value === clientType.name)) {
          this.selectedCrates.push({ name: clientType.name, value: clientType.name })
        }
      })
    }
  },
  methods: {
    clearClean () {
      this.cleanCrates = []
    },
    clearDirty () {
      this.dirtyCrates = []
    },
    clearRepair () {
      this.repairCrates = []
    },
    isClientType (name) {
      return this.clientCrateTypes.some(t => t.name === name)
    },
    async submit () {
      this.selectedCrates.forEach((crateType) => {
        // Belt and braces: the inputs are hidden for a client type, but a value left
        // over from before the type was switched must not be written either.
        if (this.isClientType(crateType.value)) {
          if (this.cleanCrates[crateType.value]) {
            this.output.push({
              id_site: this.plant.id,
              status_crates: crateType.value + '__CLEAN',
              num_crates: this.cleanCrates[crateType.value],
              date: this.date.toLocaleDateString('en-CA')
            })
          }
          return
        }
        if (this.dirtyCrates[crateType.value]) {
          this.output.push(
            {
              id_site: this.plant.id,
              status_crates: crateType.value + '__DIRTY',
              num_crates: this.dirtyCrates[crateType.value],
              date: this.date.toLocaleDateString('en-CA')
            }
          )
        }
        if (this.cleanCrates[crateType.value]) {
          this.output.push(
            {
              id_site: this.plant.id,
              status_crates: crateType.value + '__CLEAN',
              num_crates: this.cleanCrates[crateType.value],
              date: this.date.toLocaleDateString('en-CA')
            }
          )
        }
        if (this.repairCrates[crateType.value]) {
          this.output.push(
            {
              id_site: this.plant.id,
              status_crates: crateType.value + '__REPAIR',
              num_crates: this.repairCrates[crateType.value],
              date: this.date.toLocaleDateString('en-CA')
            }
          )
        }
      })
      try {
        if (this.types.map(x => x.name).includes('5.X') && (this.selectedCrates.map(x => x.value).includes('5.2') && !this.selectedCrates.map(x => x.value).includes('5.5'))) {
          this.output.push(
            {
              id_site: this.plant.id,
              status_crates: '5.5__DIRTY',
              num_crates: 0,
              date: this.date.toLocaleDateString('en-CA')
            }
          )
          this.output.push(
            {
              id_site: this.plant.id,
              status_crates: '5.5__CLEAN',
              num_crates: 0,
              date: this.date.toLocaleDateString('en-CA')
            }
          )
          this.output.push(
            {
              id_site: this.plant.id,
              status_crates: '5.5__REPAIR',
              num_crates: 0,
              date: this.date.toLocaleDateString('en-CA')
            }
          )
        } else if (this.types.map(x => x.name).includes('5.X') && (!this.selectedCrates.map(x => x.value).includes('5.2') && this.selectedCrates.map(x => x.value).includes('5.5'))) {
          this.output.push(
            {
              id_site: this.plant.id,
              status_crates: '5.2__DIRTY',
              num_crates: 0,
              date: this.date.toLocaleDateString('en-CA')
            }
          )
          this.output.push(
            {
              id_site: this.plant.id,
              status_crates: '5.2__CLEAN',
              num_crates: 0,
              date: this.date.toLocaleDateString('en-CA')
            }
          )
          this.output.push(
            {
              id_site: this.plant.id,
              status_crates: '5.2__REPAIR',
              num_crates: 0,
              date: this.date.toLocaleDateString('en-CA')
            }
          )
        }
        this.$emit('submit', this.output)
        return
      } catch (error) {
        this.$buefy.toast.open({ type: 'is-danger', message: error.message, duration: 3000 })
      }
    }
  }
}
</script>

<style>
</style>
